import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useReducer,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import APIService from '../../services/APIService';
import { useAccessToken } from '@features/user';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

// Session related types
interface TemplateBuilderSession {
  template_builder_session_id: string;
  user_id: string;
  template_id?: string;
  current_transcript_id?: string;
  current_step: ViewState;
  current_template: Template;
  uploaded_document_s3_object_id?: string;
  modified_at: string;
  created_at: string;
  chat_history?: ChatMessage[];
}

// Storage key constant for localStorage
const TEMPLATE_BUILDER_SESSION_KEY = 'template_builder_session_id';

// Example template section structure
export interface TemplateSection {
  module_id: string;
  module_name: string;
  public_name: string;
  public_description?: string;
  extraction_prompts?: {
    semantic_prompt: string;
    keyword_prompt: string;
  };
  generation_prompt?: string;
  current_content?: string;
  isProcessing?: boolean;
  markdown?: boolean;
  module_version?: number;
}

interface Template {
  template_id?: string;
  template_name?: string;
  template_description?: string;
  modules?: TemplateSection[];
  user_id?: string;
}

// ViewState type for different stages of the template building process
type ViewState = 'upload' | 'transcript' | 'template';

// Task state types
type TaskStatus = 'idle' | 'processing' | 'completed' | 'error';

// Task state interface
interface TasksState {
  fileProcessing: TaskStatus;
  transcriptSelection: 'idle' | 'selected';
  transcriptAnalysis: TaskStatus;
}

// Task action types
type TaskAction =
  | { type: 'FILE_PROCESSING_START' }
  | { type: 'FILE_PROCESSING_COMPLETE' }
  | { type: 'FILE_PROCESSING_ERROR' }
  | { type: 'TRANSCRIPT_SELECTED'; transcriptId: string }
  | { type: 'TRANSCRIPT_RESET' }
  | { type: 'ANALYSIS_START' }
  | { type: 'ANALYSIS_COMPLETE' }
  | { type: 'ANALYSIS_ERROR' }
  | { type: 'RESET_ALL' };

// Effect type for side effects
type Effect =
  | {
      type: 'CHECK_START_ANALYSIS';
    }
  | {
      type: 'NO_EFFECT';
    };

// Task reducer result including state and effect
type ReducerResult = {
  state: TasksState;
  effect: Effect;
};

// Initial task state
const initialTasksState: TasksState = {
  fileProcessing: 'idle',
  transcriptSelection: 'idle',
  transcriptAnalysis: 'idle',
};

// Task reducer
const tasksReducer = (state: TasksState, action: TaskAction): ReducerResult => {
  console.log('tasksReducer', action);

  // Default is no effect
  let effect: Effect = { type: 'NO_EFFECT' };
  let newState = { ...state };

  switch (action.type) {
    case 'FILE_PROCESSING_START':
      newState = { ...state, fileProcessing: 'processing' };
      break;
    case 'FILE_PROCESSING_COMPLETE':
      newState = { ...state, fileProcessing: 'completed' };
      // If transcript is already selected, check if we can start analysis
      if (state.transcriptSelection === 'selected') {
        effect = { type: 'CHECK_START_ANALYSIS' };
      }
      break;
    case 'FILE_PROCESSING_ERROR':
      newState = { ...state, fileProcessing: 'error' };
      break;
    case 'TRANSCRIPT_SELECTED':
      newState = { ...state, transcriptSelection: 'selected' };
      // If file processing is already complete, check if we can start analysis
      if (state.fileProcessing === 'completed') {
        effect = { type: 'CHECK_START_ANALYSIS' };
      }
      break;
    case 'TRANSCRIPT_RESET':
      newState = {
        ...state,
        transcriptSelection: 'idle',
        transcriptAnalysis: 'idle',
      };
      break;
    case 'ANALYSIS_START':
      newState = { ...state, transcriptAnalysis: 'processing' };
      break;
    case 'ANALYSIS_COMPLETE':
      newState = { ...state, transcriptAnalysis: 'completed' };
      break;
    case 'ANALYSIS_ERROR':
      newState = { ...state, transcriptAnalysis: 'error' };
      break;
    case 'RESET_ALL':
      newState = initialTasksState;
      break;
    default:
      break;
  }

  console.log('tasksReducer result', newState, effect);

  return { state: newState, effect };
};

// Simplify chat message types and interface
export interface ChatMessage {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: string | number;
  section_id?: string;
}

// Define the context state structure
interface TemplateBuilderContextState {
  // View state management
  viewState: ViewState;
  previousViewState: ViewState;
  isTransitioning: boolean;
  showContent: boolean;
  showAnalysisSuccessAnimation: boolean;
  isLoading: boolean;

  // Data
  transcriptId: string | null;
  templateId: string | null;
  sessionId: string | null;
  templateName: string | null;
  templateDescription: string | null;
  setTemplateName: React.Dispatch<React.SetStateAction<string | null>>;
  setTemplateDescription: React.Dispatch<React.SetStateAction<string | null>>;

  // Template sections
  templateSections: TemplateSection[];
  setTemplateSections: React.Dispatch<React.SetStateAction<TemplateSection[]>>;
  selectedSectionId: string | null;
  selectedSection: TemplateSection | undefined;
  processingSectionIds: string[];

  // Process states
  tasksState: TasksState;
  processingError: string | null;
  currentPollingTaskId: string | null;
  currentProcessingStage: string | null;
  cancelPolling: () => void;

  // Session management
  createSession: () => Promise<string>;
  loadSession: (sessionId: string) => Promise<void>;
  updateSession: () => Promise<void>;

  // Actions
  transitionToState: (newState: ViewState) => void;
  handleBackClick: () => void;
  handleFileUploadStarted: (taskId: string) => void;
  handleTranscriptConfirmed: (confirmedTranscriptId: string) => void;
  handleSelectSection: (sectionId: string) => void;
  handleUpdateSection: (updatedSection: TemplateSection) => void;
  handleSaveTemplate: () => void;
  handleUndoTemplate: () => Promise<void>;
  resetToStep: (step: ViewState) => void;

  // Section processing
  updateSectionsAsync: (
    sectionIds: string[],
    message?: string,
  ) => Promise<(() => void) | undefined>;

  // Chat related
  chatMessages: ChatMessage[];
  addChatMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => string;
  addAssistantMessage: (text: string, sectionId?: string) => void;
  addUserMessage: (text: string) => void;
}

// Create the context with default values
export const TemplateBuilderContext = createContext<TemplateBuilderContextState>({
  viewState: 'upload',
  previousViewState: 'upload',
  isTransitioning: false,
  showContent: true,
  showAnalysisSuccessAnimation: false,
  isLoading: false,

  transcriptId: null,
  templateId: null,
  sessionId: null,
  templateName: null,
  templateDescription: null,
  setTemplateName: () => {},
  setTemplateDescription: () => {},

  templateSections: [],
  setTemplateSections: () => {},
  selectedSectionId: null,
  selectedSection: undefined,
  processingSectionIds: [],

  tasksState: initialTasksState,
  processingError: null,
  currentPollingTaskId: null,
  currentProcessingStage: null,
  cancelPolling: () => {},

  createSession: async () => '',
  loadSession: async () => {},
  updateSession: async () => {},

  transitionToState: () => {},
  handleBackClick: () => {},
  handleFileUploadStarted: () => {},
  handleTranscriptConfirmed: () => {},
  handleSelectSection: () => {},
  handleUpdateSection: () => {},
  handleSaveTemplate: () => {},
  handleUndoTemplate: async () => {},
  resetToStep: () => {},

  updateSectionsAsync: async () => undefined,

  // Chat related
  chatMessages: [],
  addChatMessage: () => '',
  addAssistantMessage: () => {},
  addUserMessage: () => {},
});

// Props for the context provider
interface TemplateBuilderProviderProps {
  children: ReactNode;
}

// Provider component that will wrap the template builder components
export const TemplateBuilderProvider: React.FC<TemplateBuilderProviderProps> = ({ children }) => {
  const { getAccessToken } = useAccessToken();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // View state management
  const [viewState, setViewState] = useState<ViewState>('upload');
  const [previousViewState, setPreviousViewState] = useState<ViewState>('upload');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showContent, setShowContent] = useState(true);
  const [showAnalysisSuccessAnimation, setShowAnalysisSuccessAnimation] = useState(false);

  // Data states
  const [transcriptId, setTranscriptId] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [templateName, setTemplateName] = useState<string | null>(null);
  const [templateDescription, setTemplateDescription] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Template section states
  const [templateSections, setTemplateSections] = useState<TemplateSection[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [processingSectionIds, setProcessingSectionIds] = useState<string[]>([]);

  // Process states using reducer with effects
  const [tasksState, setTasksState] = useState<TasksState>(initialTasksState);

  // Process states
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [currentPollingTaskId, setCurrentPollingTaskId] = useState<string | null>(null);
  const [currentProcessingStage, setCurrentProcessingStage] = useState<string | null>(null);

  // Chat state
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);

  // Get the currently selected section
  const selectedSection = templateSections.find(section => section.module_id === selectedSectionId);

  // Session management functions
  const createSession = async (): Promise<string> => {
    try {
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIPostRequest({
        requestString: '/templatebuilder/createTemplateBuilderSession',
        body: {}, // No arguments required for session creation
        accessToken,
      });

      if (response.ok) {
        const newSessionId = response.value.session_id;
        setSessionId(newSessionId);
        // Save session ID to localStorage
        localStorage.setItem(TEMPLATE_BUILDER_SESSION_KEY, newSessionId);
        console.log('Template builder session created successfully', newSessionId);
        return newSessionId;
      } else {
        throw new Error('Failed to create session');
      }
    } catch (error) {
      console.error('Error creating session:', error);
      enqueueSnackbar('Failed to create session', { variant: 'error' });
      return '';
    }
  };

  // Check localStorage for existing session or create a new one
  useEffect(() => {
    const initializeSession = async () => {
      if (!sessionId) {
        const savedSessionId = localStorage.getItem(TEMPLATE_BUILDER_SESSION_KEY);
        if (savedSessionId) {
          await loadSession(savedSessionId);
        } else {
          createSession();
        }
      }
    };

    initializeSession();
  }, [sessionId]);

  const loadSession = async (sessionIdToLoad: string): Promise<void> => {
    try {
      const accessToken = await getAccessToken();
      // Since there's no specific endpoint mentioned for loading by ID,
      // we'll assume there's a get by ID endpoint or use a generic query
      const response = await APIService.makeAPIGetRequest({
        requestString: `/templatebuilder/getTemplateBuilderSession`,
        params: { session_id: sessionIdToLoad },
        accessToken,
      });

      if (response.ok) {
        const session: TemplateBuilderSession = response.value.session;

        // Update state with loaded session data
        setSessionId(session.template_builder_session_id);
        setViewState(session.current_step);
        setPreviousViewState(session.current_step);

        if (session.current_template) {
          setTemplateSections(session.current_template.modules || []);
          setTemplateName(session.current_template.template_name || null);
          setTemplateDescription(session.current_template.template_description || null);

          // If there are modules already, we can assume the file processing is complete
          if (session.current_template.modules) {
            setTasksState(prevState => ({
              ...prevState,
              fileProcessing: 'completed',
            }));
          }
        }

        if (session.chat_history) {
          // Convert server chat history format to our internal format
          const convertedChatHistory = session.chat_history.map((msg: any) => {
            // Parse timestamp if it's a string
            let timestamp = msg.timestamp || new Date().toISOString();
            if (typeof timestamp !== 'string') {
              timestamp = new Date(timestamp).toISOString();
            }

            // Make sure role is one of the allowed values
            const role: 'user' | 'assistant' = msg.role === 'user' ? 'user' : 'assistant';

            return {
              id: msg.id || uuidv4(),
              content: msg.content,
              role,
              timestamp,
              section_id: msg.section_id,
            } as ChatMessage;
          });

          setChatMessages(convertedChatHistory);
          console.log('Loaded chat history from session:', convertedChatHistory);
        }

        if (session.template_id) {
          setTemplateId(session.template_id);
        }

        if (session.current_transcript_id) {
          setTranscriptId(session.current_transcript_id);

          // Update task states based on the current step
          if (session.current_step === 'transcript') {
            setTasksState(prevState => ({
              ...prevState,
              fileProcessing: 'completed',
            }));
          } else if (session.current_step === 'template') {
            setTasksState(prevState => ({
              ...prevState,
              fileProcessing: 'completed',
              transcriptSelection: 'selected',
              transcriptAnalysis: 'completed',
            }));
          }
        }
      } else {
        // If loading fails, create a new session
        console.log('Failed to load session, creating a new one');
        const newSessionId = await createSession();
        if (newSessionId) {
          enqueueSnackbar('Created a new session', { variant: 'info' });
        }
      }
    } catch (error) {
      console.error('Error loading session:', error);
      // If there's an error, create a new session
      console.log('Error loading session, creating a new one');
      const newSessionId = await createSession();
      if (newSessionId) {
        enqueueSnackbar('Created a new session', { variant: 'info' });
      }
    }
  };

  const updateSession = async (): Promise<void> => {
    if (!sessionId) {
      console.log('No session to update');
      return;
    }

    try {
      const accessToken = await getAccessToken();

      // Convert chat messages to the format expected by the server
      const formattedChatHistory = chatMessages;

      // Using POST method but with the endpoint that expects PUT semantics
      const response = await APIService.makeAPIPostRequest({
        requestString: `/templatebuilder/updateTemplateBuilderSession`,
        body: {
          session_id: sessionId,
          current_step: viewState,
          current_template: {
            template_id: templateId,
            template_name: templateName,
            template_description: templateDescription,
            modules: templateSections,
          },
          current_transcript_id: transcriptId,
        },
        accessToken,
      });

      if (response.ok) {
        console.log('Session updated successfully');
      } else {
        throw new Error('Failed to update session');
      }
    } catch (error) {
      console.error('Error updating session:', error);
      enqueueSnackbar('Failed to update session state', { variant: 'error' });
    }
  };

  // Effect to update session when relevant state changes
  useEffect(() => {
    if (sessionId) {
      updateSession();
    }
  }, [viewState, templateId, transcriptId, templateSections, templateName, templateDescription]);

  // Enhanced dispatch function that handles effects
  const dispatchTasksAction = useCallback(
    (action: TaskAction) => {
      // Run the reducer to get new state and effect
      setTasksState(prevState => {
        const result = tasksReducer(prevState, action);

        // Handle the effect (after state update is committed)
        if (result.effect.type === 'CHECK_START_ANALYSIS') {
          console.log(
            'CHECK_START_ANALYSIS',
            result.state,
            action.type === 'TRANSCRIPT_SELECTED' ? action.transcriptId : transcriptId,
            templateSections.length,
          );
          // Need to use setTimeout to ensure state is updated before effect runs
          setTimeout(() => {
            const currentTranscriptId =
              action.type === 'TRANSCRIPT_SELECTED' ? action.transcriptId : transcriptId;
            if (
              result.state.fileProcessing === 'completed' &&
              result.state.transcriptSelection === 'selected' &&
              result.state.transcriptAnalysis === 'idle' &&
              currentTranscriptId &&
              templateSections.length > 0
            ) {
              startTranscriptAnalysis(currentTranscriptId);
            }
          }, 50);
        }

        return result.state;
      });
    },
    [transcriptId, templateSections.length],
  );

  // Handle state transition with animation
  const transitionToState = (newState: ViewState) => {
    if (newState === viewState) return;

    setIsTransitioning(true);
    setShowContent(false);

    // After fade out completes, change the state
    setTimeout(() => {
      setPreviousViewState(viewState);
      setViewState(newState);

      // After state change, fade the new content in
      setTimeout(() => {
        setShowContent(true);
        setIsTransitioning(false);
      }, 100);
    }, 300);
  };

  const handleBackClick = () => {
    if (viewState === 'transcript') {
      resetToStep('upload');
    } else if (viewState === 'template') {
      resetToStep('transcript');
    } else {
      navigate('/templates');
    }
  };

  // Function to poll for processing status
  const pollProcessingStatus = useCallback(
    async (taskId: string, onComplete: (result: any) => void) => {
      let pollCount = 0;
      const maxPolls = 100; // Maximum number of polling attempts
      const pollInterval = 5000; // 5 seconds between polls
      let intervalId: NodeJS.Timeout | null = null;

      setProcessingError(null);

      const checkStatus = async () => {
        try {
          // For taskIds that start with "section_update_", use mock behavior
          if (taskId.startsWith('section_update_')) {
            pollCount++;

            // Mock completion after 2-3 seconds
            if (pollCount >= 1) {
              if (intervalId) clearInterval(intervalId);

              // Create mock updated sections data
              const mockResult = {
                status: 'completed',
                data: {
                  updated_sections: processingSectionIds
                    .map(sectionId => {
                      const section = templateSections.find(s => s.module_id === sectionId);
                      if (!section) return null;

                      return {
                        module_id: section.module_id,
                        public_name: section.public_name,
                        public_description: section.public_description,
                        current_content: `Updated content for ${
                          section.public_name
                        } - ${new Date().toLocaleTimeString()}`,
                        extraction_prompts: section.extraction_prompts,
                        generation_prompt: section.generation_prompt,
                      };
                    })
                    .filter(Boolean),
                },
              };

              onComplete(mockResult.data);
              return;
            }
          } else {
            // Original behavior for other task types
            const accessToken = await getAccessToken();
            const response = await APIService.makeAPIGetRequest({
              requestString: '/templatebuilder/getProcessingStatus',
              params: { task_id: taskId },
              accessToken,
            });

            pollCount++;

            if (response.ok) {
              const result = response.value;

              // If processing is complete or has errored
              if (result.status === 'completed') {
                if (intervalId) clearInterval(intervalId);
                onComplete(result.data);
                return;
              } else if (result.status === 'error') {
                if (intervalId) clearInterval(intervalId);
                setProcessingError(result.message || 'An error occurred during processing');
                return;
              }
            }
          }

          // Check if we've reached the maximum poll attempts
          if (pollCount >= maxPolls) {
            if (intervalId) clearInterval(intervalId);
            setProcessingError('Processing timed out. Please try again.');
          }
        } catch (error) {
          console.error('Error polling for status:', error);
          if (intervalId) clearInterval(intervalId);
          setProcessingError('Failed to check processing status');
        }
      };

      // Start polling
      intervalId = setInterval(checkStatus, pollInterval);
      checkStatus(); // Initial check immediately

      // Return a function to cancel polling if needed
      return () => {
        if (intervalId) clearInterval(intervalId);
      };
    },
    [getAccessToken, processingSectionIds, templateSections],
  );

  // Start transcript analysis function
  const startTranscriptAnalysis = async (currentTranscriptId: string) => {
    try {
      // Use a new dispatch with the current state to avoid stale closures
      setTasksState(prevState => {
        const result = tasksReducer(prevState, { type: 'ANALYSIS_START' });
        return result.state;
      });

      setProcessingError(null);

      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIPostRequest({
        requestString: '/templatebuilder/startTranscriptAnalysis',
        body: {
          transcript_id: currentTranscriptId,
          session_id: sessionId,
        },
        accessToken,
      });

      if (response.ok) {
        const taskId = response.value.task_id;

        // Poll for transcript analysis results
        pollProcessingStatus(taskId, result => {
          console.log('Transcript analysis completed', result);
          if (result.module_data) {
            // Update sections from modules array if available
            if (result.module_data && Array.isArray(result.module_data)) {
              const modules = result.module_data;

              setTemplateSections(modules);

              // Select the first section by default
              if (modules.length > 0) {
                setSelectedSectionId(modules[0].id);
              }
            }
          }

          setTasksState(prevState => {
            const result = tasksReducer(prevState, {
              type: 'ANALYSIS_COMPLETE',
            });
            return result.state;
          });

          setTemplateId('template-' + currentTranscriptId);

          // Start animation sequence
          setShowContent(false);

          // Show success animation
          setTimeout(() => {
            setShowAnalysisSuccessAnimation(true);

            // After showing animation for a moment, transition to template view
            setTimeout(() => {
              setShowAnalysisSuccessAnimation(false);
              transitionToState('template');
              enqueueSnackbar('Template created successfully!', {
                variant: 'success',
              });
            }, 1500);
          }, 300);
        });
      } else {
        setProcessingError('Failed to start transcript analysis');
        setTasksState(prevState => {
          const result = tasksReducer(prevState, { type: 'ANALYSIS_ERROR' });
          return result.state;
        });
      }
    } catch (error) {
      console.error('Error starting transcript analysis:', error);
      setProcessingError('Failed to start transcript analysis');
      setTasksState(prevState => {
        const result = tasksReducer(prevState, { type: 'ANALYSIS_ERROR' });
        return result.state;
      });
    }
  };

  // Handle file upload started
  const handleFileUploadStarted = async (taskId: string) => {
    dispatchTasksAction({ type: 'FILE_PROCESSING_START' });
    transitionToState('transcript');

    // Start polling for processing status
    pollProcessingStatus(taskId, result => {
      console.log('File processing completed', result);
      if (result.template_data) {
        // Update template name and description if available
        if (result.template_data.template_name) {
          setTemplateName(result.template_data.template_name);
        }
        if (result.template_data.template_desc) {
          setTemplateDescription(result.template_data.template_desc);
        }

        // Update sections from modules array if available
        if (result.template_data.modules && Array.isArray(result.template_data.modules)) {
          const modules = result.template_data.modules;

          setTemplateSections(modules);

          // Select the first section by default if needed
          if (modules.length > 0 && !selectedSectionId) {
            setSelectedSectionId(modules[0].id);
          }
        }
      }
      dispatchTasksAction({ type: 'FILE_PROCESSING_COMPLETE' });
      enqueueSnackbar('File processing completed', { variant: 'success' });
    });
  };

  // Handle transcript confirmed
  const handleTranscriptConfirmed = (confirmedTranscriptId: string) => {
    setTranscriptId(confirmedTranscriptId);

    // Use setTimeout to ensure state is updated before dispatching
    setTimeout(() => {
      dispatchTasksAction({
        type: 'TRANSCRIPT_SELECTED',
        transcriptId: confirmedTranscriptId,
      });
    }, 50);

    if (tasksState.fileProcessing !== 'completed') {
      enqueueSnackbar('Waiting for file processing to complete', {
        variant: 'info',
      });
    }
  };

  // Handle section selection
  const handleSelectSection = (sectionId: string) => {
    setSelectedSectionId(sectionId);
  };

  // Handle section update
  const handleUpdateSection = (updatedSection: TemplateSection) => {
    setTemplateSections(prevSections =>
      prevSections.map(section =>
        section.module_id === updatedSection.module_id ? updatedSection : section,
      ),
    );

    // No need to manually call updateSession here as the useEffect will handle it
  };

  // Reset to a specific step
  const resetToStep = (step: ViewState) => {
    if (step === 'upload') {
      // Reset everything
      dispatchTasksAction({ type: 'RESET_ALL' });
      setTemplateSections([]);
      setSelectedSectionId(null);
      setTranscriptId(null);
      setTemplateId(null);
    } else if (step === 'transcript') {
      // Reset transcript selection and analysis states
      dispatchTasksAction({ type: 'TRANSCRIPT_RESET' });

      // Keep template sections from file but clear any selection
      setSelectedSectionId(null);
    }

    transitionToState(step);
  };

  // Handle template save
  const handleSaveTemplate = async () => {
    console.log('Saving template', sessionId);

    const accessToken = await getAccessToken();
    const response = await APIService.makeAPIPostRequest({
      requestString: '/templatebuilder/saveTemplate',
      body: {
        session_id: sessionId,
      },
      accessToken,
    });

    console.log('Response', response);

    if (response.ok) {
      // Clear the session ID from localStorage
      localStorage.removeItem(TEMPLATE_BUILDER_SESSION_KEY);

      enqueueSnackbar('Template saved successfully!', { variant: 'success' });
      navigate('/templates');
    } else {
      enqueueSnackbar('Failed to save template', { variant: 'error' });
    }
  };

  // Handle template undo
  const handleUndoTemplate = async () => {
    if (!sessionId) {
      enqueueSnackbar('No active session to undo', { variant: 'error' });
      return;
    }

    try {
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIPostRequest({
        requestString: '/templatebuilder/undoTemplateChange',
        body: {
          session_id: sessionId,
        },
        accessToken,
      });

      if (response.ok) {
        const { template } = response.value;

        // Update the template sections with the reverted state
        if (template.modules) {
          setTemplateSections(template.modules);
        }

        // Update template name and description if they exist
        if (template.template_name) {
          setTemplateName(template.template_name);
        }
        if (template.template_description) {
          setTemplateDescription(template.template_description);
        }

        enqueueSnackbar('Changes undone successfully', { variant: 'success' });
      } else {
        throw new Error(response.error?.message || 'Failed to undo changes');
      }
    } catch (error) {
      console.error('Error undoing template changes:', error);
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to undo changes', {
        variant: 'error',
      });
    }
  };

  // Chat functions
  const addChatMessage = useCallback((message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    const newMessage: ChatMessage = {
      ...message,
      id: uuidv4(),
      timestamp: new Date().toISOString(),
    };
    setChatMessages(prev => [...prev, newMessage]);
    return newMessage.id;
  }, []);

  const addAssistantMessage = useCallback(
    (text: string, sectionId?: string) => {
      const id = addChatMessage({
        content: text,
        role: 'assistant',
        section_id: sectionId,
      });
      return id;
    },
    [addChatMessage],
  );

  const addUserMessage = useCallback(
    (text: string) => {
      const id = addChatMessage({
        content: text,
        role: 'user',
      });
      return id;
    },
    [addChatMessage],
  );

  // Function to cancel polling
  const cancelPolling = useCallback(() => {
    if (currentPollingTaskId) {
      console.log('Cancelling polling for task:', currentPollingTaskId);
      setCurrentPollingTaskId(null);
      setCurrentProcessingStage(null);

      // Clear processing sections
      setProcessingSectionIds([]);

      enqueueSnackbar('Operation canceled', { variant: 'info' });
    }
  }, [currentPollingTaskId, enqueueSnackbar]);

  // Function to update sections asynchronously
  const updateSectionsAsync = async (sectionIds: string[], userMessage?: string) => {
    try {
      let userMessageId = null;

      // If there's a message, add it to the chat
      if (userMessage) {
        const id = addUserMessage(userMessage);
        userMessageId = id;
      }

      // Make the API call to start customization
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIPostRequest({
        requestString: '/templatebuilder/customizeTemplate',
        body: {
          session_id: sessionId,
          section_ids: sectionIds,
          user_message: userMessage || '',
          user_message_id: userMessageId,
          selected_module_id: selectedSectionId,
        },
        accessToken,
      });

      if (!response.ok) {
        throw new Error('Failed to start template customization');
      }

      const taskId = response.value.task_id;
      console.log('Started customization task:', taskId);

      // Store the current task ID
      setCurrentPollingTaskId(taskId);

      // Set up polling interval
      const pollInterval = 3000; // 3 seconds
      const maxPolls = 40; // 2 minutes max
      let pollCount = 0;

      // Chat history tracking to avoid duplicates
      const processedMessageIds = new Set<string>();

      // Function to poll for updates
      const pollForUpdates = async () => {
        if (pollCount >= maxPolls) {
          console.log('Max polling attempts reached');
          clearInterval(intervalId);

          // Clear processing states
          setProcessingSectionIds(prev => prev.filter(id => !sectionIds.includes(id)));

          // Clear current task ID and stage
          setCurrentPollingTaskId(null);
          setCurrentProcessingStage(null);

          return;
        }

        try {
          pollCount++;
          console.log(`Polling attempt #${pollCount} for task: ${taskId}`);

          const accessToken = await getAccessToken();
          const response = await APIService.makeAPIGetRequest({
            requestString: '/templatebuilder/getProcessingStatus',
            params: { task_id: taskId },
            accessToken,
          });

          if (!response.ok) {
            throw new Error('Failed to get processing status');
          }

          const result = response.value;
          console.log('Received update:', result);

          // Process based on status
          if (result.status === 'processing') {
            // Handle processing updates
            const data = result.data || {};

            // Handle stage-specific data
            if (data.stage) {
              console.log(`Current stage: ${data.stage}, progress: ${data.progress || 0}%`);

              // Update current processing stage
              setCurrentProcessingStage(data.stage);

              // Handle identified modules in stage_2
              if (
                data.stage === 'stage_2_identifying_modules' &&
                data.payload?.affected_modules &&
                Array.isArray(data.payload.affected_modules)
              ) {
                // Update UI to only show affected modules as processing
                const affectedModuleIds = data.payload.affected_modules.map(
                  (m: any) => m.module_id,
                );

                console.log('Affected modules identified:', affectedModuleIds);

                // Update only to affected modules
                setProcessingSectionIds(affectedModuleIds);
              }

              // Handle module changes in stage_4
              if (data.stage === 'stage_4_applying_changes') {
                console.log(`Processing changes in ${data.stage}`);

                // Handle updated template if available
                if (data.payload?.updated_template) {
                  console.log('Updated template received:', data.payload.updated_template);
                }
              }

              // Handle generated content in stage_5
              if (
                data.stage === 'stage_5_generating_content' &&
                data.payload?.generated_modules &&
                Array.isArray(data.payload.generated_modules)
              ) {
                console.log('Generated content received in stage 5');
                updateSections(data.payload.generated_modules);
              }

              // Process chat history updates if available in any stage
              if (data.payload) {
                // Handle deleted modules if available
                if (data.payload?.deleted_modules && Array.isArray(data.payload.deleted_modules)) {
                  console.log('Modules deleted:', data.payload.deleted_modules);

                  // Filter out deleted modules from templateSections
                  setTemplateSections(prevSections =>
                    prevSections.filter(
                      section => !data.payload.deleted_modules.includes(section.module_id),
                    ),
                  );
                }

                // Then check standard location: payload.chat_history
                if (
                  Array.isArray(data.payload.chat_history) &&
                  data.payload.chat_history.length > 0
                ) {
                  console.log('Found chat_history in payload:', data.payload.chat_history);
                  processChatHistory(data.payload.chat_history);
                }
              }
            }
          } else if (result.status === 'completed') {
            console.log('Task completed successfully');
            clearInterval(intervalId);

            // Clear current task ID and stage
            setCurrentPollingTaskId(null);
            setCurrentProcessingStage(null);

            // Process final result
            if (result.data?.payload) {
              // Final chat history updates
              if ('chat_history' in result.data.payload) {
                console.log('Processing final chat history');
                const chatHistory = result.data.payload.chat_history;

                if (Array.isArray(chatHistory) && chatHistory.length > 0) {
                  processChatHistory(chatHistory);
                  console.log('Chat history processed from final result');
                } else {
                  console.log('No chat history in final payload', result.data.payload);
                }
              }

              // Handle deleted modules if available
              if (
                result.data.payload?.deleted_modules &&
                Array.isArray(result.data.payload.deleted_modules)
              ) {
                console.log('Modules deleted:', result.data.payload.deleted_modules);

                // Filter out deleted modules from templateSections
                setTemplateSections(prevSections =>
                  prevSections.filter(
                    section => !result.data.payload.deleted_modules.includes(section.module_id),
                  ),
                );
              }

              // Update sections from final template
              if (result.data.payload.final_template?.modules) {
                updateSections(result.data.payload.final_template.modules);
              }
            }

            // Clear processing state
            setProcessingSectionIds([]);

            enqueueSnackbar('Template updated successfully', {
              variant: 'success',
            });
          } else if (result.status === 'error') {
            console.error('Task error:', result.message);
            clearInterval(intervalId);

            // Clear current task ID and stage
            setCurrentPollingTaskId(null);
            setCurrentProcessingStage(null);

            enqueueSnackbar('Error updating template', { variant: 'error' });
          }
        } catch (error) {
          console.error('Error polling for updates:', error);

          // After several consecutive errors, give up
          if (pollCount > 5) {
            clearInterval(intervalId);

            // Clear current task ID and stage
            setCurrentPollingTaskId(null);
            setCurrentProcessingStage(null);

            enqueueSnackbar('Error checking status', { variant: 'error' });
          }
        }
      };

      // Helper function to process chat history updates
      const processChatHistory = (newChatHistory: Array<ChatMessage>) => {
        if (!Array.isArray(newChatHistory) || newChatHistory.length === 0) {
          return;
        }

        setChatMessages(prevMessages => {
          // Get all existing message IDs for easy lookup
          const existingMessageIds = new Set(prevMessages.map(msg => msg.id));

          // Filter out messages we already have
          const newMessages = newChatHistory.filter(msg => !existingMessageIds.has(msg.id));

          if (newMessages.length === 0) {
            return prevMessages;
          }

          // Return combined messages, sorted by timestamp
          return [...prevMessages, ...newMessages].sort((a, b) => {
            const timeA =
              typeof a.timestamp === 'string' ? new Date(a.timestamp).getTime() : a.timestamp;
            const timeB =
              typeof b.timestamp === 'string' ? new Date(b.timestamp).getTime() : b.timestamp;
            return timeA - timeB;
          });
        });
      };

      // Helper function to update template sections
      const updateSections = (updatedSections: Array<any>) => {
        if (!Array.isArray(updatedSections)) return;

        setTemplateSections(prevSections => {
          // First, update existing sections
          const updatedExistingSections = prevSections.map(section => {
            const updatedSection = updatedSections.find(
              updated => updated.module_id === section.module_id,
            );

            if (updatedSection) {
              return {
                ...section,
                ...updatedSection,
              };
            }

            return section;
          });

          // Find new sections that don't exist in the current sections
          const existingSectionIds = new Set(prevSections.map(section => section.module_id));
          const newSections = updatedSections.filter(
            section => !existingSectionIds.has(section.module_id),
          );

          // Return combined array of updated existing sections and new sections
          return [...updatedExistingSections, ...newSections];
        });
      };

      // Start polling immediately
      await pollForUpdates();

      // Then set up interval for continued polling
      const intervalId = setInterval(pollForUpdates, pollInterval);

      // Return a cleanup function to clear the interval if component unmounts
      return () => {
        clearInterval(intervalId);
        setCurrentPollingTaskId(null);
      };
    } catch (error) {
      console.error('Error updating sections:', error);

      // Remove sections from processing state on error
      setProcessingSectionIds(prev => prev.filter(id => !sectionIds.includes(id)));

      // Clear current task ID and stage
      setCurrentPollingTaskId(null);
      setCurrentProcessingStage(null);

      enqueueSnackbar('Failed to update sections', { variant: 'error' });
    }
  };

  // When starting a new TemplateBuilder session, add initial welcome message
  useEffect(() => {
    if (sessionId && chatMessages.length === 0) {
      addAssistantMessage(
        "Hello! Let's get this template exactly the way you want it. What would you like to see changed? Feel free to mention any sections by name, describe what you'd like to change, or ask me to add a new section.",
      );
    }
  }, [sessionId, chatMessages.length, addAssistantMessage]);

  // Compile all context values
  const contextValue: TemplateBuilderContextState = {
    viewState,
    previousViewState,
    isTransitioning,
    showContent,
    showAnalysisSuccessAnimation,
    isLoading,

    transcriptId,
    templateId,
    sessionId,
    templateName,
    templateDescription,
    setTemplateName,
    setTemplateDescription,

    templateSections,
    setTemplateSections,
    selectedSectionId,
    selectedSection,
    processingSectionIds,

    tasksState,
    processingError,
    currentPollingTaskId,
    currentProcessingStage,
    cancelPolling,

    createSession,
    loadSession,
    updateSession,
    updateSectionsAsync,

    transitionToState,
    handleBackClick,
    handleFileUploadStarted,
    handleTranscriptConfirmed,
    handleSelectSection,
    handleUpdateSection,
    handleSaveTemplate,
    handleUndoTemplate,
    resetToStep,

    // Chat related
    chatMessages,
    addChatMessage,
    addAssistantMessage,
    addUserMessage,
  };

  return (
    <TemplateBuilderContext.Provider value={contextValue}>
      {children}
    </TemplateBuilderContext.Provider>
  );
};

// Custom hook for using the TemplateBuilderContext
export const useTemplateBuilder = () => {
  const context = useContext(TemplateBuilderContext);
  if (context === undefined) {
    throw new Error('useTemplateBuilder must be used within a TemplateBuilderProvider');
  }
  return context;
};
